import apiClient from './apiClient';

// ============================================================================
// TypeScript Interfaces
// ============================================================================

export type AccountType = 'CHECKING' | 'SAVINGS';
export type AccountCurrency = 'DOP' | 'USD' | 'EUR';
export type OwnershipType = 'PERSONAL' | 'JOINT' | 'CORPORATE';

export interface CustomerBankAccountRequest {
    bankId: string;
    accountLastFour: string;
    routingLastFour: string;
    accountType?: AccountType;
    accountCurrency?: AccountCurrency;
    ownershipType?: OwnershipType;
    accountNickname?: string;
}

export interface CustomerBankAccountResponse {
    id: number;
    bankId: string;
    bankName: string;
    bankCode: string;
    accountLastFour: string;
    routingLastFour: string;
    accountType: string | null;
    accountCurrency: string | null;
    ownershipType: string | null;
    accountNickname: string | null;
    isVerified: boolean;
    verifiedAt: string | null;
    createdAt: string;
}

export interface BankAccountSecureData {
    bankId: string;
    accountNumber: string;
    routingNumber: string;
    accountType?: AccountType;
}

interface ApiResponse<T> {
    success: boolean;
    data: T;
    message?: string;
}

class BankAccountService {
    async listBankAccounts(applicationId: string): Promise<CustomerBankAccountResponse[]> {
        try {
            const response = await apiClient.get<ApiResponse<CustomerBankAccountResponse[]>>(
                `/applications/${applicationId}/bank-accounts`
            );
            return response.data.data || [];
        } catch (error: any) {
            console.error('Failed to list bank accounts:', error);
            throw error;
        }
    }

    async addBankAccount(
        applicationId: string,
        data: CustomerBankAccountRequest
    ): Promise<CustomerBankAccountResponse> {
        try {
            const response = await apiClient.post<ApiResponse<CustomerBankAccountResponse>>(
                `/applications/${applicationId}/bank-accounts`,
                data
            );
            return response.data.data;
        } catch (error: any) {
            console.error('Failed to add bank account:', error);
            throw error;
        }
    }

    /** PUT uses accountId (number) not bankId */
    async updateBankAccount(
        applicationId: string,
        accountId: number,
        data: CustomerBankAccountRequest
    ): Promise<CustomerBankAccountResponse> {
        try {
            const response = await apiClient.put<ApiResponse<CustomerBankAccountResponse>>(
                `/applications/${applicationId}/bank-accounts/${accountId}`,
                data
            );
            return response.data.data;
        } catch (error: any) {
            console.error('Failed to update bank account:', error);
            throw error;
        }
    }

    /** DELETE uses accountId (number) not bankId */
    async deleteBankAccount(applicationId: string, accountId: number): Promise<void> {
        try {
            await apiClient.delete(`/applications/${applicationId}/bank-accounts/${accountId}`);
        } catch (error: any) {
            console.error('Failed to delete bank account:', error);
            throw error;
        }
    }

    validateLastFour(value: string): { valid: boolean; error?: string } {
        if (!value) return { valid: false, error: 'Required field' };
        if (value.length !== 4) return { valid: false, error: 'Must be exactly 4 digits' };
        if (!/^\d{4}$/.test(value)) return { valid: false, error: 'Must contain only numbers' };
        return { valid: true };
    }

    validateFullNumberEnding(fullNumber: string, expectedLast4: string): boolean {
        if (!fullNumber || fullNumber.length < 4) return false;
        return fullNumber.slice(-4) === expectedLast4;
    }

    validateRoutingNumber(routingNumber: string): { valid: boolean; error?: string } {
        if (!routingNumber) return { valid: false, error: 'Required field' };
        if (!/^\d{9}$/.test(routingNumber)) return { valid: false, error: 'Must be exactly 9 digits' };
        return { valid: true };
    }
}

const bankAccountService = new BankAccountService();
export default bankAccountService;